import {
  AssessmentInstrument,
  AssessmentOwnResponse,
  AssessmentWindow,
} from 'src/api/assessments';

/* The loneliness measure's copy (Norwegian bokmål, no em-dashes) and the
   small calculations the screens share. Product copy spells "Circle" with a
   capital C. */

/** The windows the screens show, in programme order. Follow-up comes with a template. */
export const SHOWN_WINDOWS: readonly AssessmentWindow[] = [
  'BEFORE',
  'MID',
  'AFTER',
];

export const WINDOW_LABEL: Record<AssessmentWindow, string> = {
  BEFORE: 'Ved oppstart',
  MID: 'Midtveis',
  AFTER: 'Etter programmet',
  FOLLOW_UP: 'Oppfølging',
};

/** The line above the questions, per window. */
export const WINDOW_KICKER: Record<AssessmentWindow, string> = {
  BEFORE: 'Før programmet starter',
  MID: 'Halvveis i programmet',
  AFTER: 'Etter programmet',
  FOLLOW_UP: 'Noen måneder etter programmet',
};

export const TAB_LABEL = 'Måling';

export const MEMBER_TITLE = 'Dine svar';
export const MEMBER_INTRO =
  'Tre spørsmål, tre ganger i løpet av programmet. Tallet går fra 3 (aldri) til 15 (svært ofte). Det er ditt eget: lederen ser bare et samlet tall for gruppen.';

export const LEADER_TITLE = 'Gruppen samlet';
export const LEADER_INTRO =
  'Snittet av gruppens svar, fra 3 (aldri ensom) til 15 (svært ofte). Du ser aldri hva den enkelte har svart, og tallene vises først når minst fem har svart.';

export const RESEARCH_LABEL = 'Svarene mine kan brukes i forskning';
export const RESEARCH_TEXT =
  'Uten navn, sammen med svar fra andre grupper. Du kan slå dette av når som helst, også for svar du allerede har gitt.';

export const CONSENT_SERVICE_TEXT =
  'Jeg forstår at svarene er en del av programmet. Institusjonen som tilbyr programmet er ansvarlig for opplysningene, og Dunbar148 behandler dem på deres vegne.';
export const CONSENT_RESEARCH_TEXT =
  'Svarene mine kan brukes i forskning, uten navn. Valgfritt, og jeg kan ombestemme meg senere under Måling.';

export const SUBMIT_LABEL = 'Send svar';
export const LATER_LABEL = 'Svar senere';
export const FINAL_NOTE = 'Svarene kan ikke endres etter at de er sendt.';
export const ANSWER_NOW_LABEL = 'Svar nå';
export const DURATION_NOTE = 'Tar om lag ett minutt.';

export const LOADING_TEXT = 'Henter målingen ...';
export const UNAVAILABLE_TEXT = 'Kunne ikke hente målingen akkurat nå.';
export const SUBMIT_FAILED_TEXT = 'Svarene ble ikke sendt. Prøv igjen.';
export const SUBMITTED_TEXT = 'Takk, svarene dine er lagret.';
export const CONSENT_SAVED_TEXT = 'Valget ditt er lagret.';
export const CONSENT_FAILED_TEXT = 'Valget ble ikke lagret. Prøv igjen.';

export const OWN_ANSWERS_TITLE = (window: AssessmentWindow): string =>
  `Svarene dine ${WINDOW_LABEL[window].toLowerCase()}`;

export const CHANGE_TITLE = 'Endring fra oppstart til etter programmet';
export const CHANGE_TEXT =
  'Regnes for dem som har svart begge ganger. Vises når minst fem har gjort det.';

export const LEGEND_TEXT =
  'Hva betyr tallene? Lav er 3 til 6, middels 7 til 10, høy 11 til 15. I SHoT 2026 svarte 18,5 prosent av studentene at de ofte savner noen å være sammen med.';

/** The bar's spoken form: how many answered in each band. */
export const bandsLabel = (bands: Record<ScoreBand, number>): string =>
  `Fordeling: ${bands.low} lav, ${bands.middle} middels, ${bands.high} høy`;

export const bothTimesText = (n: number): string =>
  `${n} har svart begge ganger.`;

export const withheldText = (minN: number): string =>
  `Vises når minst ${minN} har svart.`;

export const answeredText = (n: number, members: number): string =>
  `${n} av ${members} har svart`;

export const notOpenText = 'Ikke åpnet ennå';
export const openText = 'Åpen for svar';

/** "8,6" from 8.57: one decimal, Norwegian comma. */
export const formatMean = (mean: number): string =>
  (Math.round(mean * 10) / 10).toFixed(1).replace('.', ',');

/** "+1,2" or "-0,8" for a change; a change of zero reads "0,0". */
export const formatChange = (change: number): string => {
  const text = formatMean(Math.abs(change));
  if (Math.round(change * 10) === 0) {
    return '0,0';
  }
  return change > 0 ? `+${text}` : `-${text}`;
};

export type ScoreBand = 'low' | 'middle' | 'high';

export const BAND_LABEL: Record<ScoreBand, string> = {
  low: 'Lav',
  middle: 'Middels',
  high: 'Høy',
};

/**
 * Low, middle or high for a total score: the range cut in thirds, the top
 * third taking the remainder (3 to 6, 7 to 10, 11 to 15 for the 3 to 15 scale).
 */
export const scoreBand = (
  score: number,
  scoreMin: number,
  scoreMax: number
): ScoreBand => {
  const third = Math.floor((scoreMax - scoreMin + 1) / 3);
  if (score < scoreMin + third) {
    return 'low';
  }
  if (score < scoreMin + 2 * third) {
    return 'middle';
  }
  return 'high';
};

/** How many answered in each band, from the summary's count per score. */
export const bandCounts = (
  distribution: Record<string, number>,
  scoreMin: number,
  scoreMax: number
): Record<ScoreBand, number> => {
  const counts: Record<ScoreBand, number> = { low: 0, middle: 0, high: 0 };
  Object.entries(distribution).forEach(([score, count]) => {
    const value = Number(score);
    if (!Number.isFinite(value)) {
      return;
    }
    counts[scoreBand(value, scoreMin, scoreMax)] += count;
  });
  return counts;
};

/** The answers a member can give next: the first shown window without an answer. */
export const nextWindow = (
  responses: readonly AssessmentOwnResponse[]
): AssessmentWindow | null => {
  const answered = new Set(responses.map((response) => response.window));
  return SHOWN_WINDOWS.find((window) => !answered.has(window)) ?? null;
};

export const responseFor = (
  responses: readonly AssessmentOwnResponse[],
  window: AssessmentWindow
): AssessmentOwnResponse | undefined =>
  responses.find((response) => response.window === window);

/** The scale word for a member's answer, for example "Av og til". */
export const answerLabel = (
  instrument: AssessmentInstrument,
  value: number
): string =>
  instrument.scale.find((point) => point.value === value)?.label ??
  String(value);

/** The question in its short form for the member's own-answers list. */
export const shortItemText = (text: string): string =>
  text
    .replace(/^Hvor ofte føler du at du\s+/i, '')
    .replace(/^Hvor ofte føler du deg\s+/i, '')
    .replace(/\?$/, '')
    .replace(/^\S/, (first) => first.toUpperCase());
